import React, { useMemo } from "react";

/**
 * SygnalSignalMap — Animated radial waves dampening across a facility cross-section.
 * Centerpiece of the Sygnal Shield product page.
 *
 *  - Cross-section of a high-security command center
 *  - Animated incoming RF waves (red/orange) labeled GSM, LTE, 5G, WiFi, BT, GPS
 *  - Waves flatten/dissolve when hitting the Sygnal Shield perimeter
 *  - Inside: protected zone (calm green) with the Sygnal logo as "safe zone" indicator
 */

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/wi9dw8lv_sYGNALSHIELD_SS_logo_.jpg";

// Each emitter: angle (deg), label, freq band, dB, animation delay
const EMITTERS = [
  { angle: -150, label: "GSM",  band: "900 MHz",  delay: 0.0 },
  { angle: -110, label: "LTE",  band: "1.8 GHz",  delay: 0.4 },
  { angle: -70,  label: "5G",   band: "3.5 GHz",  delay: 0.8 },
  { angle: -30,  label: "WiFi", band: "2.4/5 GHz",delay: 1.2 },
  { angle: 30,   label: "BT",   band: "2.4 GHz",  delay: 1.6 },
  { angle: 70,   label: "GPS",  band: "1.575 GHz",delay: 2.0 },
];

const CX = 400;
const CY = 280;
const SHIELD_R = 150;   // shield perimeter
const SAFE_R = 130;     // inner safe zone (where logo sits)
const EMITTER_R = 320;  // emitter ring radius

const polar = (cx, cy, r, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

export const SygnalSignalMap = () => {
  const emitters = useMemo(
    () =>
      EMITTERS.map((e) => {
        const p = polar(CX, CY, EMITTER_R, e.angle);
        const labelP = polar(CX, CY, EMITTER_R + 28, e.angle);
        return { ...e, x: p.x, y: p.y, lx: labelP.x, ly: labelP.y };
      }),
    []
  );

  return (
    <div
      className="relative w-full overflow-hidden bg-[#070A11] border border-white/10"
      data-testid="sygnal-signal-map"
    >
      {/* Background grid */}
      <div className="absolute inset-0 opacity-30 grid-bg pointer-events-none" />

      {/* Tactical readouts */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
          ◉ LIVE · SPECTRUM SENTINEL
        </p>
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
          Sygnal Shield · Cross-section view
        </p>
      </div>
      <div className="absolute top-4 right-4 z-10 text-right">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0E9F6E]">
          PROTECTED
        </p>
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
          Attenuation · 100 dB
        </p>
      </div>

      <svg
        viewBox="0 0 800 560"
        className="w-full h-auto block"
        role="img"
        aria-label="Animated cross-section showing RF waves blocked by Sygnal Shield perimeter"
      >
        <defs>
          {/* Threat wave gradient (red/orange) */}
          <radialGradient id="threatGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF6A3D" stopOpacity="0" />
            <stop offset="60%" stopColor="#FF6A3D" stopOpacity="0.5" />
            <stop offset="95%" stopColor="#FF1F4D" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#FF1F4D" stopOpacity="0" />
          </radialGradient>

          {/* Shield glow (cyan) */}
          <radialGradient id="shieldGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#19A7E0" stopOpacity="0" />
            <stop offset="80%" stopColor="#19A7E0" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#19A7E0" stopOpacity="0.32" />
          </radialGradient>

          {/* Safe zone (green) */}
          <radialGradient id="safeGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0E9F6E" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0E9F6E" stopOpacity="0" />
          </radialGradient>

          {/* Mask: clip waves OUTSIDE the shield so they appear to flatten/disappear at perimeter */}
          <mask id="outsideShield">
            <rect x="0" y="0" width="800" height="560" fill="white" />
            <circle cx={CX} cy={CY} r={SHIELD_R - 2} fill="black" />
          </mask>

          {/* Floor pattern */}
          <pattern id="floorPattern" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
            <path d="M0 22 L22 22" stroke="rgba(25,167,224,0.18)" strokeWidth="0.6" />
          </pattern>
        </defs>

        {/* === Floor / ground line === */}
        <line x1="0" y1="430" x2="800" y2="430" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        <rect x="0" y="430" width="800" height="130" fill="url(#floorPattern)" opacity="0.6" />
        <text x="20" y="450" fill="rgba(255,255,255,0.35)" fontSize="9"
              fontFamily="ui-monospace, monospace" letterSpacing="2">
          GROUND · LEVEL 0
        </text>

        {/* === Animated incoming RF waves (masked to outside-shield only) === */}
        <g mask="url(#outsideShield)">
          {emitters.map((e, i) => (
            <g key={i}>
              {/* Three pulses per emitter, staggered */}
              {[0, 1, 2].map((k) => (
                <circle
                  key={k}
                  cx={e.x}
                  cy={e.y}
                  r="20"
                  fill="none"
                  stroke="url(#threatGrad)"
                  strokeWidth="2"
                  opacity="0"
                >
                  <animate
                    attributeName="r"
                    values="10;220"
                    dur="3.6s"
                    begin={`${e.delay + k * 1.2}s`}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;0.85;0"
                    dur="3.6s"
                    begin={`${e.delay + k * 1.2}s`}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="stroke-width"
                    values="3;0.5"
                    dur="3.6s"
                    begin={`${e.delay + k * 1.2}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
              {/* Solid dot at emitter origin */}
              <circle cx={e.x} cy={e.y} r="3.5" fill="#FF1F4D">
                <animate
                  attributeName="opacity"
                  values="0.6;1;0.6"
                  dur="1.8s"
                  begin={`${e.delay}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          ))}
        </g>

        {/* === Shield glow halo (the "wave kill" perimeter) === */}
        <circle cx={CX} cy={CY} r={SHIELD_R + 30} fill="url(#shieldGrad)" />

        {/* === Shield perimeter (animated) === */}
        <circle
          cx={CX}
          cy={CY}
          r={SHIELD_R}
          fill="none"
          stroke="#19A7E0"
          strokeWidth="2"
          strokeDasharray="6 6"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-24"
            dur="2.4s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx={CX} cy={CY} r={SHIELD_R - 6} fill="none" stroke="#19A7E0" strokeWidth="0.6" opacity="0.35" />

        {/* Tactical scan sweep (subtle pulse on the perimeter) */}
        <circle cx={CX} cy={CY} r={SHIELD_R} fill="none" stroke="#19A7E0" strokeWidth="3" opacity="0">
          <animate attributeName="opacity" values="0;0.6;0" dur="3s" repeatCount="indefinite" />
          <animate attributeName="r" values={`${SHIELD_R - 8};${SHIELD_R + 8}`} dur="3s" repeatCount="indefinite" />
        </circle>

        {/* === Facility cross-section walls (rounded room) === */}
        <g opacity="0.85">
          {/* Outer shield wall */}
          <rect
            x={CX - SHIELD_R + 10}
            y={CY - SHIELD_R + 24}
            width={(SHIELD_R - 10) * 2}
            height={SHIELD_R * 2 - 24}
            rx="14"
            fill="rgba(10,13,20,0.85)"
            stroke="#19A7E0"
            strokeWidth="1.4"
          />
          {/* Inner wall (shield layer thickness) */}
          <rect
            x={CX - SHIELD_R + 22}
            y={CY - SHIELD_R + 36}
            width={(SHIELD_R - 22) * 2}
            height={SHIELD_R * 2 - 48}
            rx="10"
            fill="none"
            stroke="rgba(25,167,224,0.45)"
            strokeWidth="0.8"
            strokeDasharray="2 3"
          />
          {/* Antenna mast on top */}
          <line x1={CX} y1={CY - SHIELD_R + 24} x2={CX} y2={CY - SHIELD_R - 8} stroke="#19A7E0" strokeWidth="1.4" />
          <circle cx={CX} cy={CY - SHIELD_R - 10} r="3" fill="#19A7E0" />
          {/* Door (bottom center) */}
          <rect
            x={CX - 18}
            y={CY + SHIELD_R - 30}
            width="36"
            height="6"
            fill="#19A7E0"
            opacity="0.65"
          />
        </g>

        {/* === Inside (safe zone) === */}
        <circle cx={CX} cy={CY} r={SAFE_R - 10} fill="url(#safeGrad)" />

        {/* Workstations / racks inside the SCIF */}
        <g opacity="0.85">
          {/* Server rack (left) */}
          <rect x={CX - 75} y={CY - 25} width="22" height="55" fill="#0E9F6E" opacity="0.18" stroke="#0E9F6E" strokeWidth="0.6" />
          <line x1={CX - 75} y1={CY - 14} x2={CX - 53} y2={CY - 14} stroke="#0E9F6E" strokeWidth="0.4" />
          <line x1={CX - 75} y1={CY - 2} x2={CX - 53} y2={CY - 2} stroke="#0E9F6E" strokeWidth="0.4" />
          <line x1={CX - 75} y1={CY + 10} x2={CX - 53} y2={CY + 10} stroke="#0E9F6E" strokeWidth="0.4" />
          <line x1={CX - 75} y1={CY + 22} x2={CX - 53} y2={CY + 22} stroke="#0E9F6E" strokeWidth="0.4" />
          {/* Console (right) */}
          <rect x={CX + 50} y={CY - 6} width="28" height="14" fill="#0E9F6E" opacity="0.2" stroke="#0E9F6E" strokeWidth="0.6" />
          <line x1={CX + 64} y1={CY + 8} x2={CX + 64} y2={CY + 24} stroke="#0E9F6E" strokeWidth="0.6" />
          <rect x={CX + 56} y={CY + 24} width="16" height="4" fill="#0E9F6E" opacity="0.4" />
        </g>

        {/* === Sygnal Logo as "safe zone" indicator === */}
        <image
          href={LOGO_URL}
          x={CX - 40}
          y={CY - 80}
          width="80"
          height="40"
          preserveAspectRatio="xMidYMid meet"
          opacity="0.95"
        />
        <text
          x={CX}
          y={CY + 60}
          textAnchor="middle"
          fill="#0E9F6E"
          fontSize="11"
          fontFamily="ui-monospace, monospace"
          letterSpacing="3"
        >
          SAFE ZONE
        </text>
        <text
          x={CX}
          y={CY + 76}
          textAnchor="middle"
          fill="rgba(255,255,255,0.5)"
          fontSize="8"
          fontFamily="ui-monospace, monospace"
          letterSpacing="2"
        >
          0 dBm · NO SIGNAL
        </text>

        {/* === Emitter labels & icons === */}
        {emitters.map((e, i) => (
          <g key={`lbl-${i}`}>
            {/* Connecting line emitter -> label */}
            <line
              x1={e.x}
              y1={e.y}
              x2={e.lx}
              y2={e.ly}
              stroke="rgba(255,31,77,0.35)"
              strokeWidth="0.6"
              strokeDasharray="2 2"
            />
            {/* Label box */}
            <g transform={`translate(${e.lx - 26}, ${e.ly - 14})`}>
              <rect
                width="52"
                height="28"
                rx="2"
                fill="rgba(10,13,20,0.92)"
                stroke="rgba(255,31,77,0.6)"
                strokeWidth="0.8"
              />
              <text
                x="26"
                y="12"
                textAnchor="middle"
                fill="#FF6A3D"
                fontSize="10"
                fontFamily="ui-monospace, monospace"
                fontWeight="600"
                letterSpacing="1.5"
              >
                {e.label}
              </text>
              <text
                x="26"
                y="22"
                textAnchor="middle"
                fill="rgba(255,255,255,0.55)"
                fontSize="7"
                fontFamily="ui-monospace, monospace"
                letterSpacing="0.6"
              >
                {e.band}
              </text>
            </g>
            {/* "BLOCKED" tick that pulses */}
            <text
              x={e.lx}
              y={e.ly + 26}
              textAnchor="middle"
              fill="#0E9F6E"
              fontSize="7.5"
              fontFamily="ui-monospace, monospace"
              letterSpacing="2"
              opacity="0"
            >
              ✕ BLOCKED
              <animate
                attributeName="opacity"
                values="0;1;0"
                dur="3.6s"
                begin={`${e.delay + 1.4}s`}
                repeatCount="indefinite"
              />
            </text>
          </g>
        ))}

        {/* === Bottom legend === */}
        <g transform="translate(20, 510)">
          <rect width="12" height="3" y="4" fill="#FF1F4D" />
          <text x="20" y="9" fill="rgba(255,255,255,0.6)" fontSize="9"
                fontFamily="ui-monospace, monospace" letterSpacing="1.5">
            INCOMING RF · UNAUTHORIZED SPECTRUM
          </text>
          <rect width="12" height="3" y="22" fill="#19A7E0" />
          <text x="20" y="27" fill="rgba(255,255,255,0.6)" fontSize="9"
                fontFamily="ui-monospace, monospace" letterSpacing="1.5">
            SYGNAL SHIELD PERIMETER · 100 dB ATTENUATION
          </text>
          <rect width="12" height="3" y="40" fill="#0E9F6E" />
          <text x="20" y="45" fill="rgba(255,255,255,0.6)" fontSize="9"
                fontFamily="ui-monospace, monospace" letterSpacing="1.5">
            PROTECTED ASSETS · SAFE ZONE
          </text>
        </g>
      </svg>
    </div>
  );
};

export default SygnalSignalMap;
